import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert
} from '@mui/material';
import { API_ENDPOINTS } from '../config/api';

type DashboardData = {
  updatedAt: string;
  summary: {
    running: number;
    queued: number;
    successRate24h: string;
    failedToday: number;
  };
  running: Array<{
    runId: string;
    device: string;
    integration: string;
    environment: string;
    startedAt: string;
    elapsedSec: number;
    phase: string;
  }>;
  recentRuns: Array<{
    runId: string;
    status: string;
    durationSec: number;
    errorType: string | null;
  }>;
  errorStats24h: Array<{ type: string; count: number }>;
  recentErrors: Array<{
    runId: string;
    errorType: string;
    step?: string;
    ts?: string;
  }>;
  history?: Array<{
    runId: string;
    status: string;
    startedAt: string;
    durationSec: number;
    errorType: string | null;
  }>;
};

const REFRESH_MS = 20000;

const getTokenFromHash = (): string => {
  const hash = window.location.hash || '';
  const query = hash.includes('?') ? hash.split('?')[1] : '';
  const params = new URLSearchParams(query);
  return params.get('token') || '';
};

const formatDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds)) return '-';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m <= 0) return `${s}s`;
  return `${m}m ${s}s`;
};

export default function CashOnlyDashboardPublic() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const token = useMemo(() => getTokenFromHash(), []);

  const fetchData = async () => {
    try {
      setError(null);
      if (!token) {
        setError('Missing token');
        return;
      }
      const url = `${API_ENDPOINTS.API_BASE}/api/public/cash-only/dashboard?token=${encodeURIComponent(token)}`;
      const res = await fetch(url, { cache: 'no-cache' });
      if (!res.ok) {
        throw new Error(`Request failed (${res.status})`);
      }
      const json = await res.json();
      setData(json);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    }
  };

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, REFRESH_MS);
    return () => clearInterval(id);
  }, [token]);

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
        <Typography variant="h4">Cash-Only Public Dashboard</Typography>
        <Typography variant="body2" color="text.secondary">
          Updated: {data?.updatedAt ? new Date(data.updatedAt).toLocaleString() : '-'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Card><CardContent>
            <Typography variant="subtitle2">Running</Typography>
            <Typography variant="h5">{data?.summary?.running ?? '-'}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card><CardContent>
            <Typography variant="subtitle2">Queued</Typography>
            <Typography variant="h5">{data?.summary?.queued ?? '-'}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card><CardContent>
            <Typography variant="subtitle2">24h Success Rate</Typography>
            <Typography variant="h5">{data?.summary?.successRate24h ?? '-'}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card><CardContent>
            <Typography variant="subtitle2">Failed Today</Typography>
            <Typography variant="h5">{data?.summary?.failedToday ?? '-'}</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Current Runs</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Run</TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell>Integration</TableCell>
                    <TableCell>Env</TableCell>
                    <TableCell>Elapsed</TableCell>
                    <TableCell>Phase</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.running || []).map((row, idx) => (
                    <TableRow key={`${row.runId}-${idx}`}>
                      <TableCell>{row.runId}</TableCell>
                      <TableCell>{row.device}</TableCell>
                      <TableCell>{row.integration}</TableCell>
                      <TableCell>{row.environment}</TableCell>
                      <TableCell>{formatDuration(row.elapsedSec)}</TableCell>
                      <TableCell>{row.phase}</TableCell>
                    </TableRow>
                  ))}
                  {(data?.running || []).length === 0 && (
                    <TableRow><TableCell colSpan={6}>No running items</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recent Runs</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Run</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.recentRuns || []).map((row) => (
                    <TableRow key={row.runId}>
                      <TableCell>{row.runId}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>{formatDuration(row.durationSec)}</TableCell>
                      <TableCell>{row.errorType || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {(data?.recentRuns || []).length === 0 && (
                    <TableRow><TableCell colSpan={4}>No recent runs</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Error Stats (24h)</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.errorStats24h || []).map((row) => (
                    <TableRow key={row.type}>
                      <TableCell>{row.type}</TableCell>
                      <TableCell>{row.count}</TableCell>
                    </TableRow>
                  ))}
                  {(data?.errorStats24h || []).length === 0 && (
                    <TableRow><TableCell colSpan={2}>No errors</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recent Errors</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Run</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Step</TableCell>
                    <TableCell>Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.recentErrors || []).map((row, idx) => (
                    <TableRow key={`${row.runId}-${idx}`}>
                      <TableCell>{row.runId}</TableCell>
                      <TableCell>{row.errorType}</TableCell>
                      <TableCell>{row.step || '-'}</TableCell>
                      <TableCell>{row.ts ? new Date(row.ts).toLocaleTimeString() : '-'}</TableCell>
                    </TableRow>
                  ))}
                  {(data?.recentErrors || []).length === 0 && (
                    <TableRow><TableCell colSpan={4}>No recent errors</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Cash-Only History (Last 50)</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Run</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.history || []).map((row) => (
                    <TableRow key={row.runId}>
                      <TableCell>{row.runId}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>{row.startedAt ? new Date(row.startedAt).toLocaleString() : '-'}</TableCell>
                      <TableCell>{formatDuration(row.durationSec)}</TableCell>
                      <TableCell>{row.errorType || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {(data?.history || []).length === 0 && (
                    <TableRow><TableCell colSpan={5}>No history</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
